import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ 
  children, 
  defaultTheme = 'dark', 
  enableSystem = true,
  disableTransitionOnChange = true,
  attribute = 'class'
}) => {
  const [theme, setTheme] = useState(defaultTheme);
  
  // Function to set theme
  const setThemeValue = (newTheme) => {
    setTheme(newTheme);
    
    // Save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', newTheme);
    }
  };
  
  // Initialize theme from localStorage or system preference
  useEffect(() => {
    // Check if we're in the browser
    if (typeof window === 'undefined') return;
    
    // Get stored theme
    const storedTheme = localStorage.getItem('theme');
    
    // If there's a stored theme, use it
    if (storedTheme) {
      setTheme(storedTheme);
    } 
    // Otherwise check system preference if enabled
    else if (enableSystem) {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setTheme(systemTheme);
    }
  }, [enableSystem]);
  
  // Apply theme to document
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const root = window.document.documentElement;
    const body = window.document.body;
    
    // Remove previous theme classes
    if (attribute === 'class') {
      root.classList.remove('light', 'dark');
      body.classList.remove('light', 'dark');
      
      root.classList.add(theme);
      body.classList.add(theme);
      
      // Apply theme-specific background and text colors directly
      if (theme === 'dark') {
        body.style.backgroundColor = '#060606';
        body.style.color = '#ffffff';
      } else {
        body.style.backgroundColor = '#ffffff';
        body.style.color = '#333333';
      }
    } else {
      root.setAttribute(attribute, theme);
      body.setAttribute(attribute, theme);
    }
    
    // Handle transition disabling
    if (disableTransitionOnChange) {
      document.documentElement.classList.add('disable-transitions');
      
      // Re-enable transitions after a short delay
      const timeout = setTimeout(() => {
        document.documentElement.classList.remove('disable-transitions');
      }, 0);
      
      return () => clearTimeout(timeout);
    }
  }, [theme, attribute, disableTransitionOnChange]);
  
  // Listen for system theme changes if enabled
  useEffect(() => {
    if (!enableSystem || typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const newTheme = e.matches ? 'dark' : 'light';
      setTheme(newTheme);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [enableSystem]);
  
  const value = {
    theme,
    setTheme: setThemeValue,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme: () => setThemeValue(theme === 'dark' ? 'light' : 'dark')
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
